"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslations } from "@/lib/use-translations";

interface DialogueFeedbackCardProps {
  topicId?: string;
  topicTitle?: string;
  traceId?: string;
  nodesExplored: number;
  onDismiss?: () => void;
}

type Rating = "bad" | "meh" | "good";
type FeedbackState = "vote" | "comment" | "thanks";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

export function DialogueFeedbackCard({
  topicId,
  topicTitle,
  traceId,
  nodesExplored,
  onDismiss,
}: DialogueFeedbackCardProps) {
  const t = useTranslations();
  const [state, setState] = useState<FeedbackState>("vote");
  const [rating, setRating] = useState<Rating | null>(null);
  const [comment, setComment] = useState("");
  const [visible, setVisible] = useState(false);
  const dismissTimer = useRef<NodeJS.Timeout | null>(null);

  // Fade in on mount
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss?.(), 300);
  }, [onDismiss]);

  const sendFeedback = useCallback(
    async (r: Rating, c?: string) => {
      try {
        await fetch(`${API_URL}/api/dialogue-feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rating: r,
            comment: c || undefined,
            traceId,
            topicId,
            topicTitle,
            nodesExplored,
          }),
        });
      } catch (err) {
        console.error("Failed to send dialogue feedback:", err);
      }
    },
    [traceId, topicId, topicTitle, nodesExplored]
  );

  const handleVote = useCallback(
    (r: Rating) => {
      setRating(r);
      sendFeedback(r);
      if (r === "good") {
        setState("thanks");
        dismissTimer.current = setTimeout(dismiss, 3000);
      } else {
        setState("comment");
      }
    },
    [sendFeedback, dismiss]
  );

  const handleSendComment = useCallback(() => {
    if (rating && comment.trim()) {
      sendFeedback(rating, comment.trim());
    }
    setState("thanks");
    dismissTimer.current = setTimeout(dismiss, 3000);
  }, [rating, comment, sendFeedback, dismiss]);

  const handleSkipComment = useCallback(() => {
    setState("thanks");
    dismissTimer.current = setTimeout(dismiss, 3000);
  }, [dismiss]);

  useEffect(() => {
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  const fb = t.dialogueFeedback;

  return (
    <div
      className={`transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
    >
      <div className="rounded-2xl px-4 py-3 shadow-sm border bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/30">
        {state === "vote" && (
          <div className="text-center space-y-2">
            <p className="text-sm font-medium text-foreground">
              {fb?.title || "What do you think of this dialogue?"}
            </p>
            <div className="flex justify-center gap-4">
              {(["bad", "meh", "good"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => handleVote(r)}
                  className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl hover:bg-muted/50 transition-colors"
                >
                  <span className="text-2xl">
                    {r === "bad" ? "\u{1F61F}" : r === "meh" ? "\u{1F610}" : "\u{1F60A}"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {fb?.[r] || r}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {state === "comment" && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground text-center">
              {fb?.tellUsWhy || "Thanks! Could you tell us why?"}
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={fb?.commentPlaceholder || "What could be better?"}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
              rows={2}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleSkipComment}
                className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {fb?.skip || "Skip"}
              </button>
              <button
                type="button"
                onClick={handleSendComment}
                disabled={!comment.trim()}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {fb?.send || "Send"}
              </button>
            </div>
          </div>
        )}

        {state === "thanks" && (
          <p className="text-sm font-medium text-foreground text-center py-1">
            {fb?.thanks || "Thanks for your feedback!"}
          </p>
        )}
      </div>
    </div>
  );
}
